import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Link as LinkIcon, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";

/** Concept-2 "New collection" dialog — also doubles as EDIT collection
 * when `initial` is passed. Field set matches the create dialog exactly
 * (per user's spec: reuse create form pre-filled for edits).
 *
 * Uses the existing backend contracts:
 *   - Create: POST /learning/collections
 *   - Edit:   PUT  /learning/collections/{id}
 * No schema change. */
const Concept2CreateCollectionDialog = ({ onClose, onCreated, initial = null }) => {
    const { depts } = useData();
    const isEdit = !!initial;
    const [name, setName] = useState(initial?.name || "");
    const [deptId, setDeptId] = useState(initial?.owner_department_id || "");
    const [description, setDescription] = useState(initial?.description || "");
    const [coverTab, setCoverTab] = useState("url"); // "url" | "upload"
    const [coverUrl, setCoverUrl] = useState(initial?.cover_image || "");
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                description: description.trim(),
                owner_department_id: deptId || null,
                cover_image: coverUrl.trim(),
            };
            let r;
            if (isEdit) {
                r = await api.put(`/learning/collections/${initial.id}`, payload);
                toast.success(`Collection "${name.trim()}" updated`);
            } else {
                r = await api.post("/learning/collections", { ...payload, department_id: deptId || undefined });
                toast.success(`Collection "${name.trim()}" created`);
            }
            onCreated?.(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isEdit ? "Failed to update collection" : "Failed to create collection"));
        } finally { setSaving(false); }
    };

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/learning/upload/cover", fd);
            const url = r.data?.url;
            if (url) {
                // The backend hands back an /api-scoped path; prepend the
                // backend origin so `<img src>` picks up the session cookie.
                const backend = process.env.REACT_APP_BACKEND_URL || "";
                setCoverUrl(url.startsWith("http") ? url : `${backend}${url}`);
            } else {
                toast.error("Upload didn't return a URL");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    return createPortal(
        <div className="lh-c2-modal-backdrop" onClick={onClose} data-testid="lh-c2-newcoll-backdrop">
            <form
                className="lh-c2-newcoll"
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                data-testid="lh-c2-newcoll"
            >
                <div className="lh-c2-newcoll-head">
                    <h3>{isEdit ? "Edit collection" : "New collection"}</h3>
                    <button type="button" className="lh-c2-modal-close" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="lh-c2-newcoll-body">
                    <div className="lh-c2-newcoll-field">
                        <label className="lh-c2-newcoll-label">Name</label>
                        <input
                            className="lh-c2-newcoll-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Sponsored Display Playbook"
                            autoFocus
                            required
                            data-testid="lh-c2-newcoll-name"
                        />
                    </div>
                    <div className="lh-c2-newcoll-field">
                        <label className="lh-c2-newcoll-label">Department</label>
                        <select
                            className="lh-c2-newcoll-input"
                            value={deptId}
                            onChange={(e) => setDeptId(e.target.value)}
                            data-testid="lh-c2-newcoll-dept"
                        >
                            <option value="">Select a department…</option>
                            {(depts || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                    <div className="lh-c2-newcoll-field">
                        <label className="lh-c2-newcoll-label">Description</label>
                        <textarea
                            className="lh-c2-newcoll-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="One line on what this collection covers"
                            rows={2}
                            data-testid="lh-c2-newcoll-desc"
                        />
                    </div>
                    <div className="lh-c2-newcoll-field">
                        <label className="lh-c2-newcoll-label">Cover image (optional)</label>
                        <div className="lh-c2-newcoll-tabs">
                            <button
                                type="button"
                                className={`lh-c2-newcoll-tab ${coverTab === "url" ? "active" : ""}`}
                                onClick={() => setCoverTab("url")}
                                data-testid="lh-c2-newcoll-tab-url"
                            >
                                <LinkIcon size={13} /> Image URL
                            </button>
                            <label
                                className={`lh-c2-newcoll-tab ${coverTab === "upload" ? "active" : ""}`}
                                data-testid="lh-c2-newcoll-tab-upload"
                            >
                                <Upload size={13} /> Upload image
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => { setCoverTab("upload"); handleUpload(e.target.files?.[0]); }}
                                />
                            </label>
                        </div>
                        {coverTab === "url" && (
                            <input
                                className="lh-c2-newcoll-input"
                                value={coverUrl}
                                onChange={(e) => setCoverUrl(e.target.value)}
                                placeholder="https://…"
                                data-testid="lh-c2-newcoll-coverurl"
                            />
                        )}
                        {coverTab === "upload" && (
                            <div className="lh-c2-newcoll-upload-hint">
                                {uploading ? "Uploading…" : (coverUrl ? "Uploaded ✓" : "Choose an image above")}
                            </div>
                        )}
                        {coverUrl && (
                            <div className="lh-c2-newcoll-cover-preview" data-testid="lh-c2-newcoll-cover-preview">
                                <img src={coverUrl} alt="Cover preview" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                <button
                                    type="button"
                                    className="lh-c2-newcoll-cover-clear"
                                    onClick={() => setCoverUrl("")}
                                    data-testid="lh-c2-newcoll-cover-clear"
                                    aria-label="Remove cover"
                                >✕</button>
                            </div>
                        )}
                        <p className="lh-c2-newcoll-hint">Recommended: 1200 × 320 px (or any 3.75:1 landscape). Leave blank to use the department's default cover.</p>
                    </div>
                </div>
                <div className="lh-c2-newcoll-footer">
                    <button type="button" className="lh-c2-btn" onClick={onClose} data-testid="lh-c2-newcoll-cancel">Cancel</button>
                    <button type="submit" className="lh-c2-btn lh-c2-btn-solid" disabled={saving || !name.trim()} data-testid="lh-c2-newcoll-submit">
                        {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create collection")}
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );
};

export default Concept2CreateCollectionDialog;
